import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Briefcase, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { Pagination } from "@/components/Pagination";
import { useToast } from "@/components/ToastProvider";
import { useUIStore } from "@/stores/ui";
import { useViewMode } from "@/features/preferences";
import {
  useJobs,
  useAddTasks,
  useUpdateJob,
  useCancelJob,
} from "@/features/jobs/hooks";
import {
  JobCard,
  JobTable,
  JobFilters,
  AddTasksForm,
  UpdateJobStatusForm,
  JobTasksModal,
  CancelJobDialog,
} from "@/features/jobs/components";
import type {
  Job,
  JobWithRelations,
  JobStatus,
  UpdateJobInput,
} from "@maker-toolchest/shared";

export function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openAddJobSidebar } = useUIStore();
  const toast = useToast();

  // View mode with persistence
  const { viewMode, setViewMode } = useViewMode('jobs');

  // Selected job state
  const [selectedJob, setSelectedJob] = useState<JobWithRelations | null>(null);

  // Modal/sidebar states
  const [isAddTasksFormOpen, setIsAddTasksFormOpen] = useState(false);
  const [isUpdateStatusFormOpen, setIsUpdateStatusFormOpen] = useState(false);
  const [isTasksModalOpen, setIsTasksModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  // Error states
  const [addTasksError, setAddTasksError] = useState<string | null>(null);
  const [updateStatusError, setUpdateStatusError] = useState<string | null>(
    null,
  );

  // Read filters from URL
  const search = searchParams.get("search") || "";
  const status = (searchParams.get("status") as JobStatus | "") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  // Helper to update a single filter in URL
  const updateFilter = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Reset to page 1 when filters change
    if (key !== "page" && key !== "view") {
      newParams.delete("page");
    }
    setSearchParams(newParams);
  };

  // Handler for page changes
  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPage.toString());
    setSearchParams(newParams);
  };

  // Build query with filters
  const query = {
    page,
    limit: 25,
    ...(search && { search }),
    ...(status && { status }),
    sortBy: "createdAt" as const,
    sortOrder: "desc" as const,
  };

  const { data, isLoading, error } = useJobs(query);

  // Mutations
  const addTasks = useAddTasks(selectedJob?.id || "");
  const updateJob = useUpdateJob(selectedJob?.id || "");
  const cancelJob = useCancelJob(selectedJob?.id || "");

  // Action handlers
  const handleAddTasks = (job: Job | JobWithRelations) => {
    setSelectedJob(job as JobWithRelations);
    setIsAddTasksFormOpen(true);
  };

  const handleUpdateStatus = (job: Job | JobWithRelations) => {
    setSelectedJob(job as JobWithRelations);
    setIsUpdateStatusFormOpen(true);
  };

  const handleViewTasks = (job: Job | JobWithRelations) => {
    setSelectedJob(job as JobWithRelations);
    setIsTasksModalOpen(true);
  };

  const handleCancelJob = (job: Job | JobWithRelations) => {
    setSelectedJob(job as JobWithRelations);
    setIsCancelDialogOpen(true);
  };

  // Form submission handlers
  const handleAddTasksSubmit = async (data: { additionalQuantity: number }) => {
    try {
      setAddTasksError(null);
      await addTasks.mutateAsync(data);
      setIsAddTasksFormOpen(false);
      setSelectedJob(null);
      toast.success(
        `Added ${data.additionalQuantity} task${data.additionalQuantity > 1 ? "s" : ""}`,
      );
    } catch (error: any) {
      setAddTasksError(error.message || "Failed to add tasks");
    }
  };

  const handleUpdateStatusSubmit = async (data: UpdateJobInput) => {
    try {
      setUpdateStatusError(null);
      await updateJob.mutateAsync(data);
      setIsUpdateStatusFormOpen(false);
      setSelectedJob(null);
      toast.success("Job status updated successfully");
    } catch (error: any) {
      setUpdateStatusError(error.message || "Failed to update job status");
    }
  };

  const handleCancelJobConfirm = async () => {
    if (!selectedJob) return;

    try {
      await cancelJob.mutateAsync(undefined);
      setIsCancelDialogOpen(false);
      setSelectedJob(null);
      toast.success("Job cancelled successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel job");
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="rounded border border-error bg-error/10 p-4 text-error">
          Failed to load jobs. Please try again.
        </div>
      </div>
    );
  }

  const jobs = data?.data || [];
  const hasFilters = search || status;

  return (
    <div className="p-4">
      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Jobs</h1>
          <p className="text-sm text-foreground-muted">
            Manage print jobs and track progress across multiple tools
          </p>
        </div>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => openAddJobSidebar()}
        >
          Create Job
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <JobFilters
          search={search}
          status={status}
          viewMode={viewMode}
          onSearchChange={(value) => updateFilter("search", value || null)}
          onStatusChange={(value) => updateFilter("status", value || null)}
          onViewModeChange={setViewMode}
        />
      </div>

      {/* Jobs list or empty state */}
      {jobs.length === 0 ? (
        <div className="rounded border border-border bg-background-subtle p-4">
          <EmptyState
            icon={<Briefcase className="h-10 w-10" />}
            title={hasFilters ? "No jobs found" : "No jobs yet"}
            description={
              hasFilters
                ? "No jobs match your filters. Try adjusting your search criteria."
                : "Create jobs to track your 3D prints, laser cuts, and other maker projects."
            }
            actionLabel={hasFilters ? undefined : "Create Job"}
            onAction={hasFilters ? undefined : () => openAddJobSidebar()}
          />
        </div>
      ) : viewMode === "grid" ? (
        <div className="space-y-2">
          {jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onAddTasks={handleAddTasks}
              onUpdateStatus={handleUpdateStatus}
              onViewTasks={handleViewTasks}
              onCancelJob={handleCancelJob}
            />
          ))}
        </div>
      ) : (
        <JobTable
          jobs={jobs}
          onAddTasks={handleAddTasks}
          onUpdateStatus={handleUpdateStatus}
          onViewTasks={handleViewTasks}
          onCancelJob={handleCancelJob}
        />
      )}

      {/* Pagination */}
      {data?.meta?.pagination && (
        <div className="mt-4">
          <Pagination
            page={data.meta.pagination.page}
            total={data.meta.pagination.total}
            limit={data.meta.pagination.limit}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Add Tasks Form */}
      {selectedJob && (
        <AddTasksForm
          open={isAddTasksFormOpen}
          onClose={() => {
            setIsAddTasksFormOpen(false);
            setSelectedJob(null);
            setAddTasksError(null);
          }}
          onSubmit={handleAddTasksSubmit}
          currentQuantity={selectedJob.quantity}
          loading={addTasks.isPending}
          error={addTasksError}
        />
      )}

      {/* Update Job Status Form */}
      {selectedJob && (
        <UpdateJobStatusForm
          open={isUpdateStatusFormOpen}
          onClose={() => {
            setIsUpdateStatusFormOpen(false);
            setSelectedJob(null);
            setUpdateStatusError(null);
          }}
          onSubmit={handleUpdateStatusSubmit}
          currentStatus={selectedJob.status}
          loading={updateJob.isPending}
          error={updateStatusError}
        />
      )}

      {/* Job Tasks Modal */}
      {selectedJob && (
        <JobTasksModal
          open={isTasksModalOpen}
          onClose={() => {
            setIsTasksModalOpen(false);
            setSelectedJob(null);
          }}
          jobId={selectedJob.id}
          jobName={selectedJob.name}
          tasks={selectedJob.tasks}
        />
      )}

      {/* Cancel Job Dialog */}
      {selectedJob && (
        <CancelJobDialog
          open={isCancelDialogOpen}
          onClose={() => {
            setIsCancelDialogOpen(false);
            setSelectedJob(null);
          }}
          onConfirm={handleCancelJobConfirm}
          job={selectedJob}
          loading={cancelJob.isPending}
        />
      )}
    </div>
  );
}
